import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, AlertTitle, CircularProgress, Chip, Stack, Paper,
  Grid, Divider, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import PublicIcon from '@mui/icons-material/Public';

// 클라우드 계정 현황 — 우리가 어떤 계정을 몇 개 갖고 있고, 어느 제품이, 누가,
// 어느 리전에서 쓰는가. 데이터: GET /api/cloud/inventory
//
// 🔴 이 화면의 가장 중요한 문장은 맨 위 경고다.
//   RAYTeams 계열이 11개 리전에 데이터를 두는 것은 **국외이전을 하지 않기 위한
//   구조**다. 숫자만 보면 정확히 거꾸로 읽힌다 — 실제로 그렇게 오독한 적이 있어
//   각 계정에 "왜 그 리전인지"(region_why)를 함께 보여준다.
//
// 🔴 금액을 그리지 않는다. 피드에 밴드만 들어 있고 원본은 재무 보관이다.
//   숫자가 없으므로 크기는 4단 게이지로 표현한다.
//
// 🔴 모르는 것을 아는 것처럼 그리지 않는다.
//   미확인·미매핑·유휴를 색과 칩으로 드러내고, 마지막 절에 "아직 세지 못한 것"을
//   따로 둔다. 이 화면이 전부인 것처럼 보이면 그 순간 거짓이 된다.

const BANDS = ['<100', '100-1k', '1k-5k', '5k+'];
const CSP_LABEL = {
  aws: 'AWS', gcp: 'GCP', cloudflare: 'Cloudflare', atlas: 'MongoDB Atlas', yandex: 'Yandex Cloud',
};

// 피드가 쓰는 status 어휘. 여기 없는 값이 오면 화면이 조용히 어긋나므로 드러낸다.
// (cloud-feed 가 schema v1 을 유지한 채 idle → to-decommission 으로 바꿔 실제로 어긋난 적이 있다 — #209)
const KNOWN_STATUS = new Set(['active', 'to-decommission']);
const RETIRING = 'to-decommission';

// 화면이 해석할 줄 아는 totals 키. 여기 없는 키가 오면 새 신호가 생겼다는 뜻이고,
// 모른 채 지나가면 그 신호는 영영 안 보인다 — 각주로 드러낸다.
const KNOWN_TOTALS = new Set([
  'accounts', 'by_csp', 'measured', 'idle', 'unmapped', 'effectively_unused', 'billed_not_declared',
]);

// 전월 대비 증감. 피드의 값은 **완료된 두 달**만 비교한 것이다 —
// 진행 중인 달을 섞으면 매달 초 전 계정이 -98% 로 찍힌다(2026-09-03 실측).
function Trend({ pct }) {
  if (pct === null || pct === undefined) return null;
  const big = Math.abs(pct) >= 20;
  const up = pct > 0;
  return (
    <Tooltip title="직전 완료 월 대비 — 진행 중인 달은 계산에서 제외합니다">
      <Chip
        size="small"
        variant={big ? 'filled' : 'outlined'}
        color={big ? (up ? 'warning' : 'default') : 'default'}
        label={`${up ? '▲' : pct < 0 ? '▼' : ''} ${Math.abs(pct).toFixed(1)}%`}
        sx={{ height: 19, fontSize: 10.5, fontFamily: 'monospace' }}
      />
    </Tooltip>
  );
}

function CostBand({ band }) {
  const theme = useTheme();
  const idx = BANDS.indexOf(band);
  if (idx < 0) {
    return <Chip size="small" variant="outlined" label="비용 미확인"
      sx={{ height: 20, fontSize: 11, borderStyle: 'dashed', color: 'text.disabled' }} />;
  }
  const shades = [theme.palette.grey[500], theme.palette.info.light,
    theme.palette.info.main, theme.palette.info.dark];
  return (
    <Tooltip title="금액이 아니라 구간만 표시합니다 — 원본은 재무 보관">
      <Stack direction="row" spacing={0.7} alignItems="center">
        <Stack direction="row" spacing={0.25}>
          {BANDS.map((_, i) => (
            <Box key={i} sx={{
              width: 6, height: 14,
              bgcolor: i <= idx ? shades[idx] : theme.palette.action.disabledBackground,
            }} />
          ))}
        </Stack>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
          {band.replace('-', '–')}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function AccountCard({ a }) {
  const theme = useTheme();
  // 🔴 "유휴" 는 피드에서 사라진 어휘다. 폐기 예정(to-decommission)이 그 자리를 대신한다.
  //   status === 'idle' 로 두면 판정이 영구 false 가 되어 폐기 예정 계정이
  //   운영 계정과 똑같이 보인다 (#209).
  const retiring = a.status === RETIRING;
  const unknownStatus = a.status && !KNOWN_STATUS.has(a.status);
  const unmapped = !a.products || a.products.length === 0;
  const regions = a.measured_regions || [];

  return (
    <Paper variant="outlined" sx={{
      p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.2,
      bgcolor: retiring ? 'action.hover' : 'background.paper',
      borderColor: retiring ? 'divider' : undefined,
    }}>
      <Stack direction="row" flexWrap="wrap" alignItems="baseline" gap={1}>
        <Typography sx={{
          fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em',
          color: retiring ? 'text.secondary' : 'text.primary',
        }}>
          {a.display || a.billing_name || a.id}
        </Typography>
        <Chip size="small" variant="outlined" label={CSP_LABEL[a.csp] || a.csp}
          sx={{ height: 19, fontSize: 10, fontWeight: 700, letterSpacing: '.06em' }} />
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.disabled' }}>
          {String(a.id).replace(/^[a-z]+-/, '').slice(0, 12)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {retiring && (
          <Tooltip title="폐기 예정으로 선언된 계정입니다">
            <Chip size="small" label="폐기 예정" sx={{ height: 19, fontSize: 10 }} />
          </Tooltip>
        )}
        {unknownStatus && (
          <Tooltip title="화면이 모르는 status 값입니다 — 피드 어휘가 바뀌었을 수 있습니다">
            <Chip size="small" variant="outlined" color="warning" label={`status: ${a.status}`}
              sx={{ height: 19, fontSize: 10 }} />
          </Tooltip>
        )}
        {unmapped && (
          <Chip size="small" variant="outlined" color="warning" label="제품 미매핑"
            sx={{ height: 19, fontSize: 10 }} />
        )}
        {/* 🔴 이름은 살아 있는데 실제로는 아무것도 안 돌고 있다는 신호.
            "장애복구 백업 전용" 같은 이름이 여기 걸리면 그 자체가 발견이다. */}
        {a.effectively_unused && (
          <Tooltip title="청구는 되지만 3개월 사용액이 사실상 0입니다 — 이름대로 동작하고 있는지 확인이 필요합니다">
            <Chip size="small" variant="outlined" color="error" label="사실상 미사용"
              sx={{ height: 19, fontSize: 10, fontWeight: 700 }} />
          </Tooltip>
        )}
        {a.no_usage && !a.effectively_unused && (
          <Chip size="small" variant="outlined" label="사용 이력 없음"
            sx={{ height: 19, fontSize: 10, borderStyle: 'dashed' }} />
        )}
        <Trend pct={a.cost_mom_pct} />
        <CostBand band={a.cost_band} />
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={{ xs: 0.5, sm: 2 }}
        sx={{ fontSize: 13, color: 'text.secondary' }}>
        {!unmapped && <span>제품 <b>{a.products.join(', ')}</b></span>}
        {a.owner && <span>담당 <b>{a.owner.split('@')[0]}</b></span>}
        {a.data_class && a.data_class !== 'None' && <span>등급 <b>{a.data_class}</b></span>}
        {a.contract_party && a.contract_party !== 'RAY' && (
          <span>계약주체 <b>{a.contract_party}</b></span>
        )}
      </Stack>

      {regions.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.6} alignItems="center">
          <PublicIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
          {regions.map((r, i) => (
            <Chip key={r.code} size="small" label={r.label}
              variant={i === 0 ? 'outlined' : 'filled'}
              color={i === 0 ? 'success' : 'default'}
              sx={{ height: 20, fontSize: 11.5, fontWeight: i === 0 ? 700 : 400 }} />
          ))}
        </Stack>
      )}

      {a.region_why && (
        <Typography sx={{
          fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary',
          pl: 1.2, borderLeft: 2, borderColor: 'divider',
        }}>
          {a.region_why}
        </Typography>
      )}

      {a.top_services?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={{ xs: 0.4, sm: 1.5 }}
          sx={{ fontSize: 11.5, color: 'text.disabled' }}>
          {a.top_services.map((s) => (
            <span key={s.name}><b style={{ color: theme.palette.text.secondary }}>{s.name}</b> {s.share}%</span>
          ))}
        </Stack>
      )}

      {a.note && (
        <Typography sx={{
          fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary', mt: 'auto',
          bgcolor: 'action.hover', px: 1.5, py: 1,
          borderLeft: 2, borderColor: /🔴|없습니다|미확인|빠져/.test(a.note) ? 'error.main' : 'warning.main',
        }}>
          {a.note}
        </Typography>
      )}
    </Paper>
  );
}

export default function CloudPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch('/api/cloud/inventory')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { hint: j.hint });
        return j;
      })
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    if (!data?.accounts) return [];
    const by = new Map();
    for (const a of data.accounts) {
      if (!by.has(a.csp)) by.set(a.csp, []);
      by.get(a.csp).push(a);
    }
    // 폐기 예정은 뒤로, 나머지는 비용 밴드 큰 순
    for (const list of by.values()) {
      list.sort((x, y) => {
        if ((x.status === RETIRING) !== (y.status === RETIRING)) return x.status === RETIRING ? 1 : -1;
        return BANDS.indexOf(y.cost_band) - BANDS.indexOf(x.cost_band);
      });
    }
    return [...by.entries()];
  }, [data]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (err) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        <AlertTitle>{err.message}</AlertTitle>
        {err.hint}
      </Alert>
    );
  }

  const t = data.totals || {};
  const oc = data.org_cost || {};
  // 🔴 수집기가 남긴 stale·키 만료를 읽는다. 플래그를 남겨도 읽는 쪽이 없으면
  //    실패는 조용히 묻히고 이 화면은 낡은 값을 최신처럼 보여준다.
  const bh = data.billing_health || {};
  const billedNotDeclared = t.billed_not_declared || [];
  const unknownTotals = Object.keys(t).filter((k) => !KNOWN_TOTALS.has(k));

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <CloudQueueIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          클라우드 계정 현황
        </Typography>
      </Stack>
      <Typography sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 680 }}>
        우리가 어떤 클라우드 계정을 몇 개 갖고 있고, 어느 제품이, 누가, 어느 리전에서
        쓰는가. 청구서{data.billing_month ? ` (${data.billing_month})` : ''}와 선언을 합쳐 만듭니다.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 3 }}>
        {[
          ['전체 계정', t.accounts, false],
          ...Object.entries(t.by_csp || {}).map(([k, v]) => [CSP_LABEL[k] || k, v, false]),
          ['제품 미매핑', t.unmapped, true],
          ['사실상 미사용', t.effectively_unused, true],
        ].map(([label, val, flag]) => (
          <Grid item xs={6} sm={4} md={3} key={label}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography sx={{
                fontSize: 26, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.1,
                color: flag && val > 0 ? 'warning.main' : 'text.primary',
              }}>
                {val ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 🔴 수집이 멈췄으면 그 사실이 값보다 먼저 보여야 한다.
          no-clobber 가드가 직전 정상 데이터를 유지하기 때문에, 이 배너가 없으면
          화면은 정상처럼 보이고 값만 조용히 낡는다. */}
      {bh.stale && (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 750 }}>비용 수집이 멈춰 있습니다</AlertTitle>
          <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
            아래 비용 값은 <b>{bh.measured_at} 수집분이며 최신이 아닙니다.</b> 마지막 시도
            {bh.last_error_at ? ` (${bh.last_error_at})` : ''}가 실패했고, 직전 정상 결과를
            유지하고 있습니다.
          </Typography>
          {bh.last_error && (
            <Typography sx={{
              fontSize: 12, fontFamily: 'monospace', mt: 1,
              opacity: 0.9, wordBreak: 'break-all',
            }}>
              {String(bh.last_error).slice(0, 240)}
            </Typography>
          )}
        </Alert>
      )}

      {/* 🔴 키 만료는 401 로만 드러난다. 드러난 뒤에는 이미 수집이 멈춘 뒤다. */}
      {(bh.key_state === 'expiring' || bh.key_state === 'expired') && (
        <Alert severity={bh.key_state === 'expired' ? 'error' : 'warning'}
          variant="outlined" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 750 }}>
            {bh.key_state === 'expired'
              ? '빌링 API 키가 만료됐습니다'
              : `빌링 API 키가 ${bh.key_days_left}일 뒤 만료됩니다`}
          </AlertTitle>
          <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
            만료 예정 <b>{bh.key_expires}</b>. 메가존에 갱신을 요청하고 저장소 시크릿
            <code> MZC_BILLING_API_KEY </code>를 교체해야 합니다. 만료되면 수집이 멈추고
            이 화면은 마지막 값을 계속 보여줍니다.
          </Typography>
        </Alert>
      )}

      {/* 비용 축 — 금액이 아니라 밴드·증감률만. 원본은 재무 보관이다. */}
      {oc.billing_month && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 750 }}>
              AWS 통합 빌링 · {oc.billing_month}
            </Typography>
            <Chip size="small" variant="outlined" color="success" label="자동 수집"
              sx={{ height: 19, fontSize: 10, fontWeight: 700 }} />
            <Box sx={{ flex: 1 }} />
            <Trend pct={oc.mom_change_pct} />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={{ xs: 1, sm: 3 }}
            sx={{ fontSize: 13, color: 'text.secondary' }}>
            <span>월평균 <b>{oc.monthly_avg_band || '—'}</b></span>
            {oc.current_month_estimate_band && (
              <Tooltip title="진행 중인 달의 예측치입니다 — 추세 계산에는 넣지 않습니다">
                <span>이번 달({oc.current_month}) 예측 <b>{oc.current_month_estimate_band}</b></span>
              </Tooltip>
            )}
            {oc.months_observed?.length > 0 && (
              <span>관측 <b>{oc.months_observed.length}</b>개월</span>
            )}
          </Stack>
        </Paper>
      )}

      {/* 🔴 청구는 되는데 우리 자산 목록에 없는 계정 — 점검을 한 번도 안 받는다 */}
      {billedNotDeclared.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 750 }}>청구되지만 자산 목록에 없는 계정</AlertTitle>
          <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
            통합 빌링에는 있는데 선언 SSOT 에 없습니다. 이 계정들은 <b>CloudTrail · IAM · 백업
            점검을 한 번도 받지 않습니다.</b> 등록하거나 폐쇄해야 합니다.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.7} sx={{ mt: 1 }}>
            {billedNotDeclared.map((id) => (
              <Chip key={id} size="small" color="warning" variant="outlined" label={id}
                sx={{ fontFamily: 'monospace', fontSize: 11.5 }} />
            ))}
          </Stack>
        </Alert>
      )}

      {/* 🔴 이 화면에서 가장 중요한 문장 */}
      <Alert severity="error" variant="outlined" icon={false} sx={{ mb: 3 }}>
        <AlertTitle sx={{ fontWeight: 750 }}>리전이 많은 것을 위반으로 읽지 마십시오</AlertTitle>
        <Typography sx={{ fontSize: 14, lineHeight: 1.7, mb: 1 }}>
          RAYTeams 계열이 여러 리전에 데이터를 두는 것은 <b>국외이전을 하지 않기 위한 구조</b>입니다.
          독일 사용자의 데이터는 프랑크푸르트에, 일본 사용자의 데이터는 도쿄에 앉습니다.
          리전마다 db · cognito · s3 · lambda 를 각각 구축했습니다.
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
          <b>하나에 모아뒀다면 그것이 위반이었습니다.</b> 청구서만 보면 정확히 거꾸로 읽히므로,
          각 계정에 <b>왜 그 리전인지</b>를 함께 적었습니다.
        </Typography>
      </Alert>

      {groups.map(([csp, list]) => (
        <Box key={csp} sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase',
            color: 'text.disabled', mb: 1,
          }}>
            {CSP_LABEL[csp] || csp} · {list.length}개
          </Typography>
          <Grid container spacing={1.5}>
            {list.map((a) => (
              <Grid item xs={12} md={6} key={a.id}>
                <AccountCard a={a} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.8 }}>
        비용은 금액이 아니라 <b>구간</b>으로만 표시합니다 — 원본은 재무 보관이며 저장소에 두지 않습니다.<br />
        리전은 <b>저장소 서비스(S3 · DynamoDB 등) 사용이 확인된 곳</b>만 셉니다 — 전송·글로벌 과금은 제외합니다.<br />
        증감률은 <b>완료된 두 달</b>만 비교합니다 — 진행 중인 달을 섞으면 매달 초 전 계정이 급감으로 보입니다.<br />
        AWS 비용은 <b>메가존 HyperBilling Open API</b> 로 자동 수집합니다. Cost Explorer 직접 호출은 payer 계정이 막고 있어 쓰지 못합니다.<br />
        선언 SSOT <code>operations/registers/cloud-assets.yaml</code> · 생성기 <code>scripts/csp-scan/build_cloud_feed.py</code>
      </Typography>

      {/* 🔴 피드가 새 신호를 실어 왔는데 화면이 모르면, 그 신호는 없는 것과 같아진다.
          schema 문자열은 어휘 변화를 잡아주지 않는다 (#209). */}
      {unknownTotals.length > 0 && (
        <Typography sx={{ fontSize: 12, color: 'warning.main', mt: 1.5, lineHeight: 1.7 }}>
          이 화면이 아직 해석하지 못하는 피드 항목이 있습니다 —{' '}
          {unknownTotals.map((k, i) => (
            <span key={k}>{i > 0 && ', '}<code>{k}</code></span>
          ))}
          . 값이 있어도 위에 그리지 않았습니다.
        </Typography>
      )}
    </Box>
  );
}
